import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  hasFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import { leadStatus, leadStatusOrder } from '@/theme/status';
import type { LeadCard } from '@/types/crm';
import type { EstadoLead } from '@/types/domain';
import {
  MOTIVO_MANUAL_POR_DEFECTO,
  contactLabel,
  describeLeadMotivo,
  originLabel,
} from './crm-format';
import { LEAD_STATUS_FORM_FIELDS, updateLeadStatusPayload } from './crm-payload';
import { CRM_LIMITS, validateStatusForm } from './crm-validation';
import { useUpdateLeadStatus } from './use-crm';

/**
 * Reclasificación MANUAL de un lead (`PATCH .../crm/leads/:conversationId/estado`).
 *
 * Es la única escritura del CRM abierta a cualquier miembro del negocio: el clasificador se
 * equivoca (el cliente pidió por Instagram, habló por teléfono…) y el vendedor lo corrige sin
 * depender del admin. Al guardar, el backend deja `estadoLeadOrigen = 'MANUAL'` y una fila en
 * `lead_status_history`, así que el detalle puede explicar quién lo movió y por qué.
 *
 * Los 4 destinos del `Select` son TODOS los estados (acá no hay máquina de estados: mover un lead a
 * cualquier parte es válido). Lo que sí responde el backend es 409 si la conversación ya estaba en
 * ese estado Y reclasificada a mano: en ese caso el mensaje se muestra tal cual, porque el problema
 * es que no hay nada que cambiar.
 *
 * El motivo es opcional en el contrato; si no se escribe, el backend guarda
 * `MOTIVO_MANUAL_POR_DEFECTO` — que la pantalla nombra para que nadie se sorprenda del texto que va
 * a aparecer en el historial.
 */

export interface LeadStatusDialogProps {
  open: boolean;
  onClose: () => void;
  /** Lead a mover (`null` = diálogo cerrado). */
  lead: LeadCard | null;
}


export function LeadStatusDialog({ open, onClose, lead }: LeadStatusDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const updateStatus = useUpdateLeadStatus(tenantId);

  const [estado, setEstado] = useState<EstadoLead | ''>('');
  const [motivo, setMotivo] = useState('');
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca limpia (el estado se elige a mano, no viene preseleccionado). */
  useEffect(() => {
    if (!open) return;

    setEstado('');
    setMotivo('');
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, lead]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!lead) return;

    const nextErrors = validateStatusForm(estado, motivo);
    if (hasFormErrors(nextErrors)) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await updateStatus.mutateAsync({
        conversationId: lead.conversationId,
        payload: updateLeadStatusPayload(estado as EstadoLead, motivo),
      });
      onClose();
    } catch (error) {
      setErrors(formErrorsFromApiError(error, LEAD_STATUS_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  if (!lead) return null;

  const actual = leadStatus[lead.estadoLead];
  const esElMismoManual = estado === lead.estadoLead && lead.estadoLeadOrigen === 'MANUAL';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reclasificar el lead"
      description={`${contactLabel(lead)} · hoy está en «${actual.label}»`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateStatus.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="lead-status-form" loading={updateStatus.isPending}>
            Guardar el estado
          </Button>
        </>
      }
    >
      <form id="lead-status-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-caption text-ink-muted">Estado actual:</span>
          <StatusBadge label={actual.label} tone={actual.tone} />
          <span className="text-micro text-ink-faint">
            {originLabel[lead.estadoLeadOrigen]} · {describeLeadMotivo(lead.estadoLeadMotivo)}
          </span>
        </div>

        <Field
          label="Nuevo estado"
          htmlFor="leadEstado"
          required
          hint="Al guardar queda marcado como «Manual»: el clasificador automático no lo vuelve a tocar hasta que lo devuelvas a automático."
          error={fieldError(errors, 'estadoLead')}
        >
          <Select
            id="leadEstado"
            name="estadoLead"
            value={estado}
            invalid={Boolean(fieldError(errors, 'estadoLead'))}
            onChange={(event) => setEstado(event.target.value as EstadoLead | '')}
          >
            <option value="">Elige el estado…</option>
            {leadStatusOrder.map((item) => (
              <option key={item} value={item}>
                {leadStatus[item].label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Motivo"
          htmlFor="leadMotivo"
          hint={`Queda en el historial del lead (máx. ${CRM_LIMITS.motivo} caracteres). Si lo dejas vacío se guarda «${MOTIVO_MANUAL_POR_DEFECTO}».`}
          error={fieldError(errors, 'motivo')}
        >
          <Textarea
            id="leadMotivo"
            name="motivo"
            rows={3}
            maxLength={CRM_LIMITS.motivo}
            placeholder="Ej. el cliente cerró por teléfono"
            value={motivo}
            invalid={Boolean(fieldError(errors, 'motivo'))}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </Field>

        {esElMismoManual ? (
          <Alert tone="warning">
            Este lead YA está en «{actual.label}» y reclasificado a mano: el backend responde «ya está
            en ese estado» y no habrá nada que guardar. Elige otro estado (o usa el recálculo si lo
            que quieres es devolverlo al clasificador).
          </Alert>
        ) : null}
      </form>
    </Modal>
  );
}

